import Swiper from 'swiper';
import Mustache from 'mustache';
import { Navigation } from 'swiper/modules';

export type ProductSelectorSettings = {
  /** Title string for 1 result, %count% will be replaced with number */
  resultsTitleSingular: string;
  /** Title string for many results, %count% will be replaced with number */
  resultsTitlePlural: string;
  /** Product slider previous button text */
  sliderPrevButtonText: string;
  /** Product slider next button text */
  sliderNextButtonText: string;
  /** Class name for the form loading state */
  formLoadingClass: string;
  /** Product card mustache template */
  productCardTemplate: string;
  /** Dom elements needed */
  elements: {
    /** Filter form element */
    form: HTMLFormElement | null;
    /** Results container element */
    resultsContainer: HTMLElement | null;
    /** Results title element */
    resultsTitle: HTMLElement | null;
    /** Results navigation element */
    resultsNavContainer: HTMLElement | null;
    /** Results products list element */
    productsContainer: HTMLElement | null;
  }
};

export default class ProductSelector {
  settings: ProductSelectorSettings;
  productSwiper: Swiper | null = null;

  constructor(container: HTMLElement, settings: Partial<ProductSelectorSettings> = {}) {
    const defaultSettings: ProductSelectorSettings = {
      resultsTitleSingular: 'Found %count% product',
      resultsTitlePlural: 'Found %count% products',
      sliderPrevButtonText: 'Previous',
      sliderNextButtonText: 'Next',
      formLoadingClass: 'loading',
      productCardTemplate: '',
      elements: {
        form: null,
        resultsContainer: null,
        resultsTitle: null,
        resultsNavContainer: null,
        productsContainer: null,
      },
    };

    this.settings = {
      ...defaultSettings,
      ...settings,
    };

    // Check for nececssary settings
    if(!this.settings.productCardTemplate) {
      throw new Error('[NHT ProductSelector]: product card template not set');
    }

    // Check for nececssary dom elements
    if(!Object.keys(this.settings.elements).every(key => this.settings.elements[key] instanceof HTMLElement)) {
      throw new Error('[NHT ProductSelector]: not all dom elements set');
    }

    console.log('nht-product-selector.init()');

    this.attachEventListeners();
    this.createProductSlider();
  }

  attachEventListeners() {
    this.settings.elements.form?.addEventListener('submit', this.onFormSubmit.bind(this));
    this.settings.elements.form?.addEventListener('reset', this.onFormReset.bind(this));
  }

  getFormData(form) {
    const formData = new FormData(form);
    const formObject = {};

    formData.forEach((value, key) => {
      // Check if multiselect, aka array
      if(key.indexOf('[]') > -1) {
        const newKey = key.replace('[]', '');
        if(!formObject[newKey]) {
          formObject[newKey] = [];
        }
        formObject[newKey].push(value);
      } else {
        formObject[key] = value;
      }
    });

    return formObject;
  }

  onFormSubmit(event: Event) {
    event.preventDefault();

    const form = event.target as HTMLFormElement;

    if(form.classList.contains(this.settings.formLoadingClass)) {
      return;
    }

    const formData = this.getFormData(form);

    this.setFormLoading(true);

    this.fetchProducts(formData)
      .then((response) => {
        this.renderProducts(response);
        this.setFormLoading(false);
        this.scrollToResults();
      })
      .catch((error) => {
        console.error('[ProductSelector]: error fetching products', error);
        this.setFormLoading(false);
      });
  }

  onFormReset(event: Event) {
    // Do nothing for now, but child classes can override this
  }

  setFormLoading(state: boolean) {
    this.settings.elements.form?.classList.toggle(this.settings.formLoadingClass, state);
  };

  async fetchProducts(formData) {
    // Add language to formData
    const apiData = {
      ...formData,
      'stage-language': document.documentElement.lang,
    };

    const queryString = Object.keys(apiData)
      .map(k => {
        const value = apiData[k];
        if(Array.isArray(value)) {
          // Handle arrays using PHP format
          return value.map(item => `${encodeURIComponent(k)}[]=${encodeURIComponent(item)}`).join('&');
        } else {
          // Handle non-array values
          return `${encodeURIComponent(k)}=${encodeURIComponent(value)}`;
        }
      })
      .join('&');

    return fetch(`/stage-ajax/nht-product-selector/?${queryString}`, {
      method: 'GET',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(response => response.json());
  }

  scrollToResults() {
    this.settings.elements?.resultsContainer?.scrollIntoView({
      behavior: 'smooth',
    });
  }

  updateResultsTitle(count: number) {
    const title = count === 1 ? this.settings.resultsTitleSingular : this.settings.resultsTitlePlural;
    const titleString = title.replace('%count%', String(count));

    if(this.settings.elements?.resultsTitle) {
      this.settings.elements.resultsTitle.innerText = titleString;
    }
  }

  renderProducts(products) {
    this.updateResultsTitle(products.length);
    this.destroyProductSlider();
    if(products.length) {
      this.createProductSlider(products);
    }
  }

  createSliderNav() {
    if(this.settings.elements?.resultsNavContainer) {
      const prevButton = document.createElement('button');
      prevButton.classList.add(...['nht-slider-prev', 'button', 'button--ghost', 'button--icon']);
      prevButton.setAttribute('aria-label', this.settings.sliderPrevButtonText);
      prevButton.innerHTML = `<span class="icon icon--arrow-left" aria-hidden="true"></span>`;

      const nextButton = document.createElement('button');
      nextButton.classList.add(...['nht-slider-next', 'button', 'button--ghost', 'button--icon']);
      nextButton.setAttribute('aria-label', this.settings.sliderNextButtonText);
      nextButton.innerHTML = `<span class="icon icon--arrow-right" aria-hidden="true"></span>`;

      this.settings.elements.resultsNavContainer.append(prevButton);
      this.settings.elements.resultsNavContainer.append(nextButton);
    }
  }

  destroyProductSlider() {
    if(this.productSwiper) {
      // Destroy slider
      this.productSwiper.destroy(true, true);

      if(this.settings.elements.productsContainer) {
        this.settings.elements.productsContainer.innerHTML = '';
      }

      // Remove navigation
      if(this.settings.elements.resultsNavContainer) {
        this.settings.elements.resultsNavContainer.innerHTML = '';
      }
    }
  }

  createProductSlider(products?: any[]) {
    // Add neccessary swiper elements
    const sliderContainer = document.createElement('div');
    sliderContainer.classList.add('swiper');
    const sliderWrapper = document.createElement('div');
    sliderWrapper.classList.add('swiper-wrapper');
    sliderContainer.append(sliderWrapper);

    // If products array is passed, it is an ajax response and should be rendered straight to slider
    // If products array is not passed, convers product grid to slider using product cards in dom
    if(products) {
      products.forEach((product) => {
        const slide = document.createElement('div');
        slide.classList.add('swiper-slide');
        slide.innerHTML = Mustache.render(this.settings.productCardTemplate, product);
        sliderWrapper.append(slide);
      });

    } else {
      // Move all dom product cards to slider as slides
      const productElements = Array.from(this.settings.elements?.productsContainer?.children || []);
      productElements.forEach((product) => {
        const slide = document.createElement('div');
        slide.classList.add('swiper-slide');
        slide.append(product);
        sliderWrapper.append(slide);
      });
    }

    this.settings.elements?.productsContainer?.append(sliderContainer);

    this.productSwiper = new Swiper(sliderContainer, {
      navigation: {
        nextEl: '.nht-slider-next',
        prevEl: '.nht-slider-prev',
      },
      modules: [Navigation],
      slidesPerView: 1,
      spaceBetween: 15,
      speed: 400,
      centeredSlides: false,
      breakpoints: {
        720: {
          slidesPerView: 2,
          slidesPerGroup: 2,
          spaceBetween: 20,
        },
        992: {
          slidesPerView: 3,
          slidesPerGroup: 3,
          spaceBetween: 30,
        },
        1200: {
          slidesPerView: 4,
          slidesPerGroup: 4,
          spaceBetween: 64,
        },
      },
      on: {
        beforeInit: () => {
          this.createSliderNav();
        },
      },
    });
  }
}
